import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useState, useEffect } from 'react';

// Specialty Count Component
const SpecialtyCount = ({ asyncFunction }) => {
  const [specialtyData, setSpecialtyData] = useState(null);

  useEffect(() => {
    if (asyncFunction) {
      (async() => {
        const data = await asyncFunction();
        setSpecialtyData(data);
      })()
    }
  }, [asyncFunction]);

  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-sm text-gray-600">
        {specialtyData ? specialtyData.length : '...'} amazing varities across our platform!
      </span>
      <span className="text-sm">🎉</span>
    </div>
  );
};

const COLORS = ["#8B7ED8", "#A78BFA", "#C4B5FD", "#DDD6FE", "#EDE9FE", "#F3F0FF", "#B794F6", "#9F7AEA", "#805AD5", "#6B46C1"];

// Custom Tooltip for Pie Chart
const CustomTooltip = ({ active, payload, total }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : '0.0';
    
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-ktx-navy">{data.name}</p>
        <p className="text-sm text-sage-green">
          <span className="font-medium">Count:</span> {data.value}
        </p>
        <p className="text-sm text-ktx-purple">
          <span className="font-medium">Percentage:</span> {percentage}%
        </p>
      </div>
    );
  }
  return null;
};

// Custom Legend Component
const CustomLegend = ({ data, total }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  
  if (!data || data.length === 0) return null;

  // Sort data by value in descending order
  const sortedData = [...data].sort((a, b) => b.value - a.value);
  const topThree = sortedData.slice(0, 3);
  const remaining = sortedData.slice(3);

  const renderLegendItem = (item, index, isTopThree = true) => {
    const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0';
    const originalIndex = data.findIndex(d => d.name === item.name);
    const isTopSpecialty = index === 0;
    
    // Calculate bar width based on percentage of total
    const barWidth = total > 0 ? (item.value / total) * 100 : 0;
    
    return (
      <div 
        key={index} 
        className={`flex items-center space-x-2 p-1.5 rounded-md transition-all duration-200 ${
          isTopSpecialty 
            ? 'bg-gradient-to-r from-green-100 to-sage-green/10 border border-sage-green/30 shadow-sm' 
            : isTopThree
            ? 'bg-sage-green/10 border border-sage-green/20'
            : 'bg-gray-50 hover:bg-sage-green/5'
        }`}
      >
        {/* Color indicator with size-based styling */}
        <div 
          className={`rounded-full flex-shrink-0 ${
            isTopSpecialty ? 'w-3 h-3 ring-1 ring-purple-300' : 
            isTopThree ? 'w-2.5 h-2.5 ring-1 ring-purple-200' : 'w-2 h-2'
          }`}
          style={{ backgroundColor: COLORS[originalIndex % COLORS.length] }}
        ></div>
        
        {/* Specialty name with size-based typography */}
        <div className="flex-1 min-w-0">
          <p className={`truncate ${
            isTopSpecialty 
              ? 'text-xs font-bold text-gray-800' 
              : isTopThree
              ? 'text-xs font-semibold text-gray-700'
              : 'text-xs font-medium text-gray-700'
          }`} title={item.name}>
            {item.name}
          </p>
        </div>
        
        {/* Visual bar representation */}
        <div className="flex-1 max-w-16">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className={`h-1.5 rounded-full transition-all duration-500 ${
                isTopSpecialty ? 'bg-gradient-to-r from-purple-500 to-purple-400' :
                isTopThree ? 'bg-purple-400' : 'bg-purple-300'
              }`}
              style={{ width: `${barWidth}%` }}
            ></div>
          </div>
        </div>
        
        {/* Count and percentage with size-based styling */}
        <div className="text-right">
          <p className={`${
            isTopSpecialty 
              ? 'text-xs font-bold text-gray-800' 
              : isTopThree
              ? 'text-xs font-semibold text-gray-700'
              : 'text-xs font-medium text-gray-700'
          }`}>
            {item.value}
          </p>
          <p className={`text-xs ${
            isTopSpecialty ? 'text-gray-600 font-medium' : 
            isTopThree ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {percentage}%
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-0 space-y-0.5">
      {/* Top 3 specialties */}
      {topThree.map((item, index) => renderLegendItem(item, index, true))}
      
      {/* Dropdown for remaining specialties */}
      {remaining.length > 0 && (
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full flex items-center justify-between p-1.5 rounded-md bg-gray-100 hover:bg-sage-green/5 border border-gray-200 transition-all duration-200"
          >
            <span className="text-xs font-medium text-gray-700">
              +{remaining.length} more specialties
            </span>
            <svg 
              className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${
                showDropdown ? 'rotate-180' : ''
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {/* Dropdown content */}
          {showDropdown && (
            <div className="mt-0.5 space-y-0.5 max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg">
              {remaining.map((item, index) => (
                <div key={index} className="px-1">
                  {renderLegendItem(item, index + 3, false)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { SpecialtyCount };

export default function PieChartDisplay({ asyncFunction }) {
  const [dynamicPieData, setDynamicPieData] = useState(null);

  useEffect(() => {
    if (asyncFunction) {
      (async() => {
        setDynamicPieData(await asyncFunction());
      })()
    }
  }, [])

  // Calculate total from the full dataset
  const total = dynamicPieData ? dynamicPieData.reduce((sum, item) => sum + item.value, 0) : 0;
  
  return (
    <>
      {asyncFunction && dynamicPieData ? (
        <div className="w-full relative">
          <ResponsiveContainer width="100%" height={330}>
            <PieChart margin={{ top: 5, right: 5, bottom: 20, left: 5 }}>
              <Pie
                data={dynamicPieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={140}
                fill="#8884d8"
                dataKey="value"
                stroke="#fff"
                strokeWidth={2}
              >
                {dynamicPieData.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip total={total} />} />
            </PieChart>
          </ResponsiveContainer>
          <CustomLegend data={dynamicPieData} total={total} />
        </div>
      ) : (
        <div className="w-full h-[400px] flex justify-center items-center">
          <div className="w-20 h-20 border-4 border-dashed rounded-full animate-spin-slow border-ktx-purple flex justify-center items-center"></div>
        </div>
      )}
    </>
  );
};

